"use client";

import { createContext, useContext, useState, useMemo, ReactNode } from "react";
import { PopulatedPayloadFormFunnel, PayloadFormQuestion } from "@portal/app/_server/services/formFunnel/payloadTypes";

export type FormAnswerValue = string | number | boolean | string[] | null;

type FormFunnelContextType = {
  funnel: PopulatedPayloadFormFunnel;
  currentStepIndex: number;
  totalSteps: number;
  answers: Record<string, FormAnswerValue>;
  setAnswer: (questionId: string, value: FormAnswerValue) => void;
  nextStep: () => void;
  prevStep: () => void;
  isSubmitting: boolean;
  setIsSubmitting: (isSubmitting: boolean) => void;
  currentQuestion?: PayloadFormQuestion;
  isComplete: boolean;
};

export const FormFunnelContext = createContext<FormFunnelContextType | undefined>(undefined);

export function useFormFunnel() {
  const context = useContext(FormFunnelContext);
  if (!context) {
    throw new Error("useFormFunnel must be used within a FormFunnelProvider");
  }
  return context;
}

interface FormFunnelProviderProps {
  funnel: PopulatedPayloadFormFunnel;
  children: ReactNode;
}

export function FormFunnelProvider({ funnel, children }: FormFunnelProviderProps) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, FormAnswerValue>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter questions that are strings (shouldn't happen with depth=2 but good for safety)
  const questions = useMemo(() => {
    return (funnel.questions || []).filter((q): q is PayloadFormQuestion => typeof q !== 'string');
  }, [funnel.questions]);

  const totalSteps = questions.length;
  const currentQuestion = questions[currentStepIndex];
  const isComplete = currentStepIndex >= totalSteps;

  const setAnswer = (questionId: string, value: FormAnswerValue) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const nextStep = () => {
    if (currentStepIndex < totalSteps) {
      setCurrentStepIndex((prev) => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((prev) => prev - 1);
    }
  };

  const value = {
    funnel,
    currentStepIndex,
    totalSteps,
    answers,
    setAnswer,
    nextStep,
    prevStep,
    isSubmitting,
    setIsSubmitting,
    currentQuestion,
    isComplete,
  };

  return (
    <FormFunnelContext.Provider value={value}>
      {children}
    </FormFunnelContext.Provider>
  );
}
